import React, { useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { TeamInvitationContent } from './team-invitation-modal-content';

// Continuum buttons live inside shadow roots — drill down to the real focused element
function getDeepActiveElement(): HTMLElement | null {
  let el: Element | null = document.activeElement;
  while (el?.shadowRoot) {
    el = el.shadowRoot.activeElement;
  }
  return el as HTMLElement | null;
}

interface TeamInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamName?: string;
  subdomain?: string;
  userName?: string;
  suggestedEmails?: string[];
}

export const TeamInvitationModal: React.FC<TeamInvitationModalProps> = ({
  isOpen,
  onClose,
  teamName = 'your team',
  subdomain = 'your-company',
  userName = 'a colleague',
  suggestedEmails = [],
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Block body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Capture trigger element synchronously before focus shifts, then focus close button
  useLayoutEffect(() => {
    if (isOpen) {
      previousFocusRef.current = getDeepActiveElement();
      closeButtonRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Restore focus to the trigger element after the modal is removed from the DOM
  const handleClose = () => {
    const trigger = previousFocusRef.current;
    onClose();
    requestAnimationFrame(() => trigger?.focus());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') handleClose();
  };

  const modalContent = (
    <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          padding: '16px',
        }}
        onClick={handleClose}
        onKeyDown={handleKeyDown}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="invitation-modal-title"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 20px',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#f7fafc',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2
              id="invitation-modal-title"
              style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 600,
                color: '#1a202c',
              }}
            >
              Invite Team to PagerDuty
            </h2>
            <button
              ref={closeButtonRef}
              onClick={handleClose}
              aria-label="Close dialog"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#718096',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e2e8f0')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              padding: '16px 20px',
              overflowY: 'auto',
              flex: 1,
            }}
          >
            <TeamInvitationContent
              teamName={teamName}
              subdomain={subdomain}
              userName={userName}
              suggestedEmails={suggestedEmails}
            />
          </div>
        </div>
      </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
